// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import breakpoints from "@guestsupply/XDL/PreStyle/breakpoints";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";

const enum fields {
    altText = "altText",
    imageLink = "imageLink",
    // Xng-Changes:
    largeImagePath = "largeImagePath",
    mediumImagePath = "mediumImagePath",
    smallImagePath = "smallImagePath",
    width = "width",
    height = "height",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.largeImagePath]: string;
        [fields.mediumImagePath]: string;
        [fields.smallImagePath]: string;
        [fields.altText]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.width]: string;
        [fields.height]: string;
    };
}

export interface ImageStyles {
    wrapper?: InjectableCss;
    imageLink?: LinkPresentationProps;
}

export const imageStyles: ImageStyles = {};

const styles = imageStyles;

const Image: FC<OwnProps> = ({
    fields: { width, height, smallImagePath, mediumImagePath, largeImagePath, imageLink, altText },
}) => {
    const { url } = useGetLink(imageLink);

    // Xng-Changes:
    const image = (
        <picture>
            <source media={`(min-width: ${breakpoints.values[3]}px)`} srcSet={largeImagePath} />
            <source media={`(min-width: ${breakpoints.values[2]}px)`} srcSet={mediumImagePath} />
            <img src={smallImagePath} alt={altText} />
        </picture>
    );

    return (
        <StyledWrapper {...styles.wrapper} {...{ width, height }} className="img-wrapper">
            {url ? (
                <Link href={url} {...styles.imageLink} aria-label={`${fields.altText}`}>
                    {image}
                </Link>
            ) : (
                image
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: Image,
    definition: {
        group: "XNGAGE" as any,
        icon: "image",
        fieldDefinitions: [
            {
                name: fields.largeImagePath,
                displayName: "Large Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },

            {
                name: fields.mediumImagePath,
                displayName: "Medium Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.smallImagePath,
                displayName: "Small Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.altText,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.imageLink,
                fieldType: "General",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.width,
                fieldType: "General",
                editorTemplate: "TextField",
                placeholder: "Enter: width style",
                defaultValue: "",
                validate: (value: string | null) => {
                    return !value ? null : CSS.supports("width", value) ? null : `Invalid Input`;
                },
            },
            {
                name: fields.height,
                fieldType: "General",
                editorTemplate: "TextField",
                placeholder: "Enter: height style",
                defaultValue: "",
                validate: (value: string | null) => {
                    return !value ? null : CSS.supports("height", value) ? null : `Invalid Input`;
                },
            },
        ],
    },
};

export default widgetModule;
